// Helpers for running Jitsi calls in a dedicated browser tab (see
// src/pages/CallWindow.js + the "/call" branch in src/index.js).
//
// `kjuup_my_active_calls` is a localStorage map of roomId -> timestamp shared
// across the main Kjuup tab and the call tab. The main tab uses it to suppress
// the "Anruf gestartet" ongoing-calls popup for a call the user just opened
// (App.js), and the call tab clears its entry on leave/close. A TTL sweep drops
// stale entries so a crashed call tab can't permanently suppress future calls.

const STORAGE_KEY = 'kjuup_my_active_calls';
// Calls rarely run longer than a few hours; anything older is treated as stale.
const TTL_MS = 6 * 60 * 60 * 1000;

function readMap() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_) {
        return {};
    }
}

function writeMap(map) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    } catch (_) { /* ignore quota/serialization errors */ }
}

function sweep(map) {
    const now = Date.now();
    let changed = false;
    for (const key of Object.keys(map)) {
        if (!map[key] || now - map[key] > TTL_MS) {
            delete map[key];
            changed = true;
        }
    }
    return changed;
}

export function markCallActive(roomId) {
    if (!roomId) return;
    const map = readMap();
    sweep(map);
    map[String(roomId)] = Date.now();
    writeMap(map);
}

export function unmarkCallActive(roomId) {
    if (!roomId) return;
    const map = readMap();
    delete map[String(roomId)];
    sweep(map);
    writeMap(map);
}

export function isCallActiveForMe(roomId) {
    if (!roomId) return false;
    const map = readMap();
    if (sweep(map)) writeMap(map);
    return Boolean(map[String(roomId)]);
}

// Opens the Jitsi call in a new browser tab. MUST be called synchronously from
// within a user gesture (e.g. a click handler, before any await) or the browser
// popup blocker may reject it. `withVideo` controls whether the camera starts
// on (video call) or off (audio call); it defaults to off.
export function openCallTab({ roomId, roomName, isGroupChat, withVideo = false }) {
    if (!roomId) return null;
    const query = new URLSearchParams({
        room: String(roomId),
        name: roomName || '',
        group: isGroupChat ? '1' : '0',
        video: withVideo ? '1' : '0',
    }).toString();
    return window.open(`/call?${query}`, '_blank');
}
